import * as React from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useData } from "../utils/DataContext";
import { apiGetPdf, handleApiError } from "../utils/ApiClient";
import { useAuth } from "react-oidc-context";
import { Irai } from "../types/GxpTypes";
import IraiDetailModal from "./IraiDetailModal";
import { useToast } from "../utils/ToastContext";
import { useSessionStorage } from "../utils/useSessionStorage";
import { fetchAndOpenPdfById } from "../utils/PdfUtils";

const IraiList: React.FC = () => {
  const { iraiData } = useData();
  const { showToast } = useToast();
  const [selectedIrai, setSelectedIrai] = React.useState<Irai | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  // sessionStorageを使用したDataGrid状態管理
  const [paginationModel, setPaginationModel] = useSessionStorage<GridPaginationModel>(
    'iraiList_pagination',
    { page: 0, pageSize: 10 }
  );
  const [sortModel, setSortModel] = useSessionStorage<GridSortModel>(
    'iraiList_sort',
    [{ field: "id", sort: "desc" }]
  );

  const navigate = useNavigate();
  const auth = useAuth();
  const styles = {
    buttonContainer: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "4px",
      padding: "4px",
      marginTop: "10px",
    },
    button: {
      minWidth: "60px",
      fontSize: "12px", // Smaller font size for buttons
      padding: "4px 4px", // Adjust padding if needed
    },
  };

  const handleRowDoubleClick = (params: GridRowParams) => {
    setSelectedIrai(params.row as Irai);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedIrai(null);
  };

  const renderOperateCell = (
    params: GridRenderCellParams,
  ) => {
    const openReport = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      navigate(`/curveReport/${params.row.id}`);
    };

    const openIraiPdf = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        await fetchAndOpenPdfById(apiGetPdf, "/getIraiForm", params.row.id, auth.user?.access_token);
      } catch (error) {
        console.error("Error opening irai PDF:", handleApiError(error, 'Opening irai PDF'));
        showToast("依頼書の取得に失敗しました。", "error");
      }
    };

    const openReportPdf = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        await fetchAndOpenPdfById(apiGetPdf, "/getReportForm", params.row.id, auth.user?.access_token);
      } catch (error) {
        console.error("Error opening report PDF:", handleApiError(error, 'Opening report PDF'));
        showToast("報告書の取得に失敗しました。", "error");
      }
    };

    const continueIrai = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      navigate("/newIrai", { state: { irai: params.row } });
    };

    const redoIrai = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      // 元のiraiオブジェクトをコピーして新しいオブジェクトを作成
      const newIrai = { ...params.row };
      newIrai.preFailureId = params.row.id;
      newIrai.id = null;
      newIrai.formNo= null;
      newIrai.specimenId = null;
      delete newIrai.createdAt;
      delete newIrai.updatedAt;
      navigate("/newIrai", { state: { irai: newIrai } });
    };

    switch (params.row.status) {
      case "入力中":
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="secondary"
              style={styles.button}
              onClick={continueIrai}
            >
              登録続行
            </Button>
          </div>
        );
      case "承認待":
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openIraiPdf}
            >
              依頼書
            </Button>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openReport}
            >
              中間報告
            </Button>
          </div>
        );
      case "承認済":
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openReportPdf}
            >
              最終報告書
            </Button>
          </div>
        );
      case "最終報告済":
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openIraiPdf}
            >
              依頼書
            </Button>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openReport}
            >
              報告書
            </Button>
          </div>
        );
      case "検査失敗":
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openIraiPdf}
            >
              依頼書
            </Button>
            <Button
              variant="contained"
              color="secondary"
              style={styles.button}
              onClick={redoIrai}
            >
              再依頼
            </Button>
          </div>
        );
      default:
        return (
          <div style={styles.buttonContainer}>
            <Button
              variant="contained"
              color="info"
              style={styles.button}
              onClick={openIraiPdf}
            >
              依頼書
            </Button>
          </div>
        );
    }
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "依頼ID", width: 120, flex: 0 },
    { 
      field: "status", 
      headerName: "進捗状況", 
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            color: params.value === "承認待" ? "#d32f2f" : "inherit",
            fontWeight: params.value === "承認待" ? "bold" : "normal",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "",
      headerName: "操作",
      sortable: false,
      filterable: false,
      width: 200, flex: 0,
      renderCell: renderOperateCell,
    },
    { field: "iraiDate", headerName: "依頼日", flex: 1 },
    { field: "approvalDoctorName", headerName: "依頼医師", flex: 1 },
    {
      field: "dataApprovalDoctorName",
      headerName: "承認医師",
      flex: 1,
    },
    {
      field: "patientName",
      headerName: "患者名",
      flex: 1,
    },
    { field: "approvalDate", headerName: "承認日", flex: 1 },
    { field: "specimenId", headerName: "検体ID", flex: 1 },
  ];

  return (
    <>
      <DataGrid
        rows={iraiData}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[10, 20, 40]}
        localeText={{
          columnMenuManageColumns: "カラム管理",
          columnMenuFilter: "絞り込み",
          columnMenuHideColumn: "カラムを非表示",
          columnMenuUnsort: "並び替えを解除",
          columnMenuSortAsc: "昇順に並び替え",
          columnMenuSortDesc: "降順に並び替え",
        }}
        rowHeight={50}
        onRowDoubleClick={handleRowDoubleClick}
        sx={{
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
      />
      <IraiDetailModal
        irai={selectedIrai}
        open={modalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

export default IraiList;